import {
  CustomMappingType,
  ICustomMapping,
  ICustomMappingSourceOption,
  IFailedCustomMappingRecord,
  IPracticeMigration,
  ITranslationMap,
} from '@principle-theorem/practice-migrations/interfaces';
import { ManualTransactionType } from '@principle-theorem/principle-core';
import { IManualTransactionType } from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  INamedDocument,
  WithPrimaryKey,
  XSLXImporterExporter,
  asyncForEach,
  multiMap,
  multiSortBy$,
  nameSorter,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, first, sortBy, uniqBy } from 'lodash';
import { scan } from 'rxjs/operators';
import Fuse from 'fuse.js';
import { BaseCustomMappingHandler } from '../../../base-custom-mapping-handler';
import { CustomMapping } from '../../../custom-mapping';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import {
  IExactV2PaymentMethod,
  PaymentMethodsV2SourceEntity,
} from '../../source/entities/payment-methods-v2';
import { PaymentMethodToXSLX } from './payment-method-to-xlsx';
import { XSLXToPaymentMethod } from './xlsx-to-payment-method';

export const PAYMENT_METHOD_CUSTOM_MAPPING_TYPE = 'exactPaymentMethodMapping';

export const PAYMENT_METHOD_MAPPING: ICustomMapping = CustomMapping.init({
  metadata: {
    label: 'Payment Methods',
    description: `
      Maps Exact receipt payment method strings (trans4.trnsref) to a
      Principle Manual Transaction Type.

      Source options are auto-detected from distinct UPPER(TRIM(trnsref))
      values on receipt rows (trnstype = 86020). Blank trnsref values are
      excluded — those receipts fall through to "Manual - No Transaction Type".

      All payment methods are mapped to TransactionProvider.Manual; the
      destination dropdown selects which per-brand Manual Transaction Type
      to use (e.g. EFTPOS → "EFTPOS", VISA → "Visa Card").
    `,
    type: PAYMENT_METHOD_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.DocumentReference,
  labelOverrides: {
    sourceIdentifier: 'Payment Method',
    sourceLabel: 'Payment Method',
    destinationIdentifier: 'Manual Transaction Type',
  },
});

export class ExactPaymentMethodMappingHandler extends BaseCustomMappingHandler<
  IManualTransactionType,
  string
> {
  customMapping = PAYMENT_METHOD_MAPPING;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const sourceEntity = new PaymentMethodsV2SourceEntity();
    const records = await sourceEntity
      .getAllRecords$(migration)
      .pipe(
        multiMap((record) => record.data),
        scan((all, batch) => [...all, ...batch], [] as IExactV2PaymentMethod[])
      )
      .toPromise();

    const options = uniqBy(
      (records ?? []).map((record) => ({
        label: sourceEntity.getSourceLabel(record),
        value: record.id,
      })),
      (option) => option.value
    );

    return sortBy(options, 'label');
  }

  async getDestinationOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<IManualTransactionType>[]> {
    return snapshot(
      ManualTransactionType.all$(migration.configuration.brand).pipe(
        multiSortBy$(nameSorter()),
        multiMap((type) => ({ name: type.name, ref: type.ref })),
        multiSortBy$(nameSorter())
      )
    );
  }

  async autocompleteMappings(
    migration: WithRef<IPracticeMigration>
  ): Promise<void> {
    const translationMap = new TranslationMapHandler(migration);
    const sourceOptions = await this.getSourceOptions(migration);
    const destinationOptions = await this.getDestinationOptions(migration);
    const existingRecords = await this.getRecords(translationMap);

    const alreadyMappedIdentifiers = existingRecords
      .filter((existingRecord) => !!existingRecord.destinationIdentifier)
      .map((existingRecord) => existingRecord.sourceIdentifier);

    const unmappedSourceOptions = sourceOptions.filter(
      (sourceOption) => !alreadyMappedIdentifiers.includes(sourceOption.value)
    );

    const fuzzyMatcher = new Fuse(destinationOptions, {
      keys: ['name'],
      includeScore: true,
      threshold: 0.3,
    });

    const recordsForUpload = unmappedSourceOptions.map((sourceOption) => {
      const matches = fuzzyMatcher.search(sourceOption.label);
      const closestMatch = first(matches)?.item;

      if (!closestMatch) {
        return;
      }

      return {
        destinationIdentifier: closestMatch.ref,
        destinationValue: { value: closestMatch.name },
        sourceIdentifier: sourceOption.value,
        sourceLabel: sourceOption.label,
      };
    });

    await this.upsertBulk(compact(recordsForUpload), translationMap);
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, paymentMethods, translators } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(
      fileName,
      paymentMethods,
      translators
    );
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, paymentMethods, translators } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(
      fileName,
      paymentMethods,
      translators
    );
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToPaymentMethod()
    );

    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await asyncForEach(records, (record) =>
      translationMap.deleteRecord(record)
    );

    const sourceOptions = await this.getSourceOptions(migration);
    const destinationOptions = await this.getDestinationOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    const recordsForUpload = items.map((item) => {
      const value = sourceOptions.find(
        (sourceOption) => sourceOption.value === item.sourceId
      )?.value;
      if (!value || !item.mapTo) {
        return;
      }

      const manualType = destinationOptions.find(
        (option) => option.name === item.mapTo
      );

      if (!manualType) {
        failedRecords.push({
          sourceIdentifier: value,
          sourceLabel: item.sourceName,
          reason: `Destination identifier "${item.mapTo?.trim()}" not found`,
        });
        return;
      }

      return {
        destinationIdentifier: manualType.ref,
        destinationValue: { value: manualType.name },
        sourceIdentifier: value,
        sourceLabel: item.sourceName,
      };
    });

    await this.upsertBulk(compact(recordsForUpload), translationMap);

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, { failedRecords });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    paymentMethods: ICustomMappingSourceOption[];
    translators: PaymentMethodToXSLX;
  }> {
    const fileName = this.getFileName();
    const paymentMethods = await this.getSourceOptions(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translators = new PaymentMethodToXSLX(
      await this.getDestinationOptions(migration),
      await this.getRecords(translationMap)
    );
    return { fileName, paymentMethods, translators };
  }
}

/**
 * Resolves a payment method string to a named Manual Transaction Type
 * using the payment-method mapping.
 *
 * The method key is normalised to UPPER(TRIM(reference)) to match
 * the identifiers stored during source sync.
 *
 * Returns undefined when `reference` is blank or no mapping exists for
 * the normalised key — the caller should fall back to plain Manual.
 */
export function resolvePaymentMethodTransactionType(
  mappings: WithPrimaryKey<ITranslationMap<IManualTransactionType, string>>[],
  reference?: string
): INamedDocument<IManualTransactionType> | undefined {
  if (!reference) {
    return;
  }
  const normalisedKey = reference.trim().toUpperCase();
  const mapping = mappings.find(
    (record) => record.sourceIdentifier === normalisedKey
  );
  if (!mapping?.destinationValue?.value || !mapping.destinationIdentifier) {
    return;
  }
  return {
    name: mapping.destinationValue.value,
    ref: mapping.destinationIdentifier,
  };
}
